import React, { useMemo, useState } from "react";
import { formatDate, formatNumberIndian, handlePayment, convertNumberToWords } from "../../../utilis/common-funtions";
import {
  getAllPaymentsFromApplicant,
  getBookingAmountTotalFromApplicant,
} from "../../../utilis/bookingPaymentAmount";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import UploadPopup from "../../../shared-components/upload-popup/UploadPopup";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

const PaymentDetails = ({
  applicantData,
  defaultShowList = true,
  isVoucherForm = false,
  showVoucherPayments = false,
  showtotalAmount = false,
  /** Booking preview (customer preview): show declaration text before transaction table */
  showBookingDeclaration = false,
  isLumpsum = false,
}) => {
  const paymentDetails = showVoucherPayments ? applicantData?.voucherPayments : defaultShowList ? applicantData?.payments : applicantData?.groupPayments;
  const { t } = useTranslation();

  const allPayments = useMemo(
    () => getAllPaymentsFromApplicant(applicantData),
    [applicantData]
  );
  const bookingAmountTotal = useMemo(
    () => getBookingAmountTotalFromApplicant(applicantData),
    [applicantData]
  );

  /** Sum for the current table only (regular / voucher / lumpsum section). Booking declaration uses `bookingAmountTotal` (all sections). */
  const totalAmount = (paymentDetails || []).reduce((sum, p) => sum + (Number(p?.paidAmount) || 0), 0);
  const showStatusColumn = showVoucherPayments;
  const hasVoucherPaymentHistory =
    Array.isArray(applicantData?.voucherPayments) &&
    applicantData.voucherPayments.length > 0;

  const sectionHeading = (() => {
    if (showVoucherPayments) return t("stepper.voucherPaymentDetails");
    if (!defaultShowList) return t("stepper.lumpsumPaymentDetails");
    if (hasVoucherPaymentHistory) return t("stepper.paymentDetailsBooking");
    return t("stepper.paymentDetails");
  })();

  const showDeclarationBlock =
    showBookingDeclaration &&
    !showVoucherPayments &&
    !isVoucherForm &&
    allPayments.length > 0;
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const toTitleCase = (str) => {
    if (str !== str.toLowerCase()) {
      return str;
    }
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleView = (docs) => {
    setSelectedImages(docs || []);
    setOpenPopup(true);
  };

  return (
    <div className="otherApplicantDetails paymentDetails">
      <div className="applicantDetailsCard">
        {showDeclarationBlock && (
          <Box sx={{ mb: 2 }}>
            <Typography
              component="p"
              className="payment-p"
              sx={{ fontSize: "14px", color: "#1C252E", lineHeight: 1.6 }}
            >
              {t("preview.paymentDeclaration.intro")}{" "}
              {t("preview.paymentDeclaration.bookingAmount")}{" "}
              <Box component="span" fontWeight={700}>
                ₹ {formatNumberIndian(Math.round(bookingAmountTotal) || 0)}/- (
                {convertNumberToWords(Math.round(bookingAmountTotal) || 0)})
              </Box>
              {isLumpsum ? (
                <>
                  {" "}
                  {t("preview.paymentDeclaration.lumpsumSuffixBefore")}
                  <Box component="span" fontWeight={700}>
                    {t("preview.paymentDeclaration.lumpsumSuffixHighlight")}
                  </Box>
                  {t("preview.paymentDeclaration.lumpsumSuffixAfter")}
                </>
              ) : (
                <>
                  {" "}
                  {allPayments.length === 1
                    ? t("preview.paymentDeclaration.paidInSingle")
                    : t("preview.paymentDeclaration.paidInMultiple")}
                </>
              )}
            </Typography>
          </Box>
        )}
        <h3 className="titlePersonalHD marginBottom28">
          {sectionHeading}
          {showtotalAmount && paymentDetails?.length > 0 && (
            <span style={{ marginLeft: "8px", fontWeight: 600 }}>
              - {t("paymentDetails.label.totalAmount", "Total Amount")}: ₹ {formatNumberIndian(totalAmount)}
            </span>
          )}
        </h3>
        <div className="tbResponsive">
          <table>
            <tr>
              <th style={{ color : "#1C252E" }}>{t("paymentDetails.label.srNo")}</th>
              <th style={{ color : "#1C252E" }}>{t("paymentDetails.label.paymentMode")}</th>
              <th style={{ color : "#1C252E" }}>{t("paymentDetails.label.beingPaid")}</th>
              <th style={{ color : "#1C252E" }}>{t("kycDocuments.labels.drawnOn")}</th>
              <th style={{ color : "#1C252E" }}>{t("paymentDetails.label.dated")}</th>
              <th style={{ color: "#1C252E" }}>{t("corporatePaymentOption.paymentOptionForm.amount")}</th>
              {showStatusColumn && <th style={{ color: "#1C252E" }}>{t("paymentDetails.label.status", "Status")}</th>}
              <th style={{ color: "#1C252E" }}>{t("corporatePaymentOption.paymentOptionForm.paymentProofLabel")}</th>
              {isVoucherForm === true && <th style={{ color: "#1C252E" }}>{t("corporatePaymentOption.paymentOptionForm.chequeDepositSlip")}</th>}
            </tr>
            {paymentDetails?.length > 0 ? (
            paymentDetails?.map((payment, index) => {
                const details = payment?.paymentDetails || {};
                const paymentDate = payment?.date || payment?.paymentDate

                return (
                    <tr key={payment.id || index}>
                    <td>{index + 1}</td>
                    <td>{payment?.paymentMode? handlePayment(payment?.paymentMode) : "N/A"}</td>
                    <td>{details?.transactionNumber || details?.gatewayPaymentId || details?.chequeNumber || "N/A"}</td>
                    <td>{details?.method?.toLowerCase().includes("upi") ? "UPI": toTitleCase(details?.method || "N/A")}</td>
                    <td>{paymentDate ? formatDate(paymentDate) : "N/A"}</td>
                    <td>
                        <span>₹&nbsp;</span>
                        <span>{formatNumberIndian(Number.parseFloat(payment?.paidAmount) || 0)}</span>
                    </td>
                    {showStatusColumn && (
                      <td>
                        {payment?.status ? payment?.status : "N/A"}
                      </td>
                    )}
                    <td>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {details?.paymentProof?.length ? (
                          <Tooltip title="View Payment Proof" placement="top" arrow>
                            <IconButton
                              onClick={() => handleView(details?.paymentProof)}
                            >
                              <DescriptionOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Payment Proof Not Available" placement="top" arrow>
                            <span>
                              <IconButton disabled>
                                <DescriptionOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </td>
                   {isVoucherForm && <td>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {details?.chequeDepositSlip?.length > 0 ? (
                          <Tooltip title="View Cheque Deposit Slip" placement="top" arrow>
                            <IconButton
                              onClick={() => handleView(details?.chequeDepositSlip)}
                            >
                              <DescriptionOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Cheque Deposit Slip Not Available" placement="top" arrow>
                            <span>
                              <IconButton disabled>
                                <DescriptionOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                          </Box>
                        </td>
                        }
                  </tr>
                );
            })) : (
              <tr>
                <td colSpan={isVoucherForm ? 9 : showStatusColumn ? 8 : 7} style={{ textAlign: "center", fontSize: "14px", padding: "16px", color: "#6b7280" }}>
                  No payment details available
                </td>
              </tr>
            )}
          </table>
        </div>
      </div>
      {openPopup && (
        <UploadPopup
          heading="View"
          totalImages={1}
          closePopup={() => setOpenPopup(false)}
          uploadedImage={selectedImages?.[0] ? [selectedImages[0]] : []}
          fieldName={"paymentProof"}
          hidePhysicalCopyOption
          dynamicSize={1}
          hideCameraOption={false}
          showDeleteIcon={false}
        />
      )}
    </div>
  );
};

export default PaymentDetails;
 